/**
 * GRN lookup dialog shown on entry to the NCR create screen. Entering a GRN
 * number auto-populates supplier, PO, part, and pricing details.
 * Extracted from NCRCreate for the 500-line file limit (CR-6387).
 */
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Loader2, Search, FileText, SkipForward } from 'lucide-react';

interface NCRGrnLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: string;
  onValueChange: (value: string) => void;
  lookingUp: boolean;
  onLookup: () => void;
  onSkip: () => void;
}

export default function NCRGrnLinkDialog({
  open, onOpenChange, value, onValueChange, lookingUp, onLookup, onSkip,
}: NCRGrnLinkDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Link a Goods Receipt Note</DialogTitle>
          <DialogDescription>
            Do you have a GRN number? Entering it will auto-populate supplier, PO, part, and pricing details.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="grn_dialog_input">GRN Number</Label>
            <div className="flex gap-2 mt-1">
              <Input
                id="grn_dialog_input"
                value={value}
                onChange={(e) => onValueChange(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onLookup(); } }}
                placeholder="e.g. GRN-620252-10"
                autoFocus
              />
              <Button onClick={onLookup} disabled={lookingUp || !value.trim()} aria-label="Look up GRN">
                {lookingUp ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onSkip} className="w-full sm:w-auto">
            <SkipForward className="h-4 w-4 mr-2" />
            Skip - No GRN
          </Button>
          <Button onClick={onLookup} disabled={lookingUp || !value.trim()} className="w-full sm:w-auto">
            {lookingUp ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Looking up...</>
            ) : (
              <><FileText className="h-4 w-4 mr-2" />Look up GRN</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
